import React, { useEffect, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import api, { formatINR, formatDistance, resolveImage } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Crown, Lock, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { MapViewSkeleton } from "../components/Skeletons";
import PageHeader from "../shared/components/PageHeader";
// v2.80 — Cross-session refresh so map markers reflect admin
// mutations (approve / delete / sold / featured) without manual reload.
import { useDataSync } from "../lib/dataSync";
// v2.80 — Mirror fullscreen toggle into URL for deep-link + back-button.
import { useUrlState } from "../shared/utils/useUrlState";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const priceIcon = (price) =>
  L.divIcon({
    className: "custom-price-icon",
    html: `<div style="background:#0055FF;color:white;font-family:Manrope,sans-serif;font-weight:700;font-size:11px;padding:4px 8px;border-radius:2px;box-shadow:0 4px 10px -3px rgba(0,85,255,0.5);white-space:nowrap;">${price}</div>`,
    iconSize: [60, 24],
    iconAnchor: [30, 12],
  });

export default function MapView() {
  const { user, isAdmin, isDealer, isBuilder, isTeamMember, tier, loading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [meta, setMeta] = useState(null);
  // Mobile-only: full-screen map toggle (hidden on md+). v2.80 — URL-backed
  // so a direct link / back-button preserves the user's choice.
  const [fullscreen, setFullscreen] = useUrlState("fs", false);

  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [fullscreen]);

  // v2.71.75 — Map View access (correctly tier-aware after audit):
  //   - Admin / Team → always unlocked, full market view
  //   - Dealer / Builder → unlocked, but see ONLY their OWN submissions
  //   - Client with ANY active paid plan ("normal" / "prime" / "premium" /
  //     "pro") → unlocked. "normal" was previously excluded — v2.71.75
  //     opened it so the entry-level paid tier (₹999/yr) gets map too.
  //   - Free clients (tier=null) → LOCKED, prompted to subscribe.
  //   - Anon → LOCKED, prompted to register + subscribe.
  // Backend mirrors this in `can_access_map()`.
  const isPaidTier = !!tier && tier !== "free" && tier !== "basic";
  const hasOverride = !!user?.can_access_map;
  const ownOnly = isDealer || isBuilder;
  const isUnlocked = isAdmin || isTeamMember || isDealer || isBuilder || isPaidTier || hasOverride;

  // v2.80 — Pull-once helper so both the initial mount AND the dataSync
  // refresh path share identical fetch logic. Memoised on `ownOnly` so
  // the useEffect deps stay stable.
  const loadMap = useCallback(() => {
    const endpoint = ownOnly ? "/dealer/my-submissions" : "/properties?for_map=true";
    api.get(endpoint).then((r) => {
      const list = ownOnly ? r.data : r.data.results;
      setProperties(list.filter((p) => p.latitude && p.longitude));
      setMeta(ownOnly ? { results: list, total: list.length } : r.data);
    }).catch(() => {});
  }, [ownOnly]);

  useEffect(() => {
    if (!isUnlocked) return;
    loadMap();
  }, [isUnlocked, ownOnly, loadMap]);

  // v2.80 — Real-time refresh on any property mutation. Lat/long-bearing
  // rows are re-pulled; scroll and zoom-level on the live map are kept
  // intact because Leaflet's MapContainer is uncontrolled.
  useDataSync(["properties"], () => { if (isUnlocked) loadMap(); });

  if (loading) {
    return <MapViewSkeleton />;
  }

  if (!isUnlocked) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 bg-[#FAFAFA]">
        <div className="max-w-md text-center bg-white border border-[#E4E4E7] p-10 rounded-sm" data-testid="map-locked">
          <Lock size={28} className="mx-auto text-[#0055FF] mb-3" />
          <h1 className="font-heading font-bold text-3xl tracking-tight">Map view is a paid feature</h1>
          <p className="text-[14px] text-[#52525B] mt-3 mb-6">
            Pick any plan (starting at ₹999) to unlock map view with price pins for every listing.
          </p>
          <Link to="/pricing">
            <Button className="btn-brand rounded-sm">
              <Crown size={14} className="mr-1.5" /> See Plans
            </Button>
          </Link>
          {!user && (
            <p className="text-[11px] text-[#A1A1AA] mt-4">
              <Link to="/register" className="text-[#0055FF] font-semibold hover:underline">Create a free account</Link> first.
            </p>
          )}
        </div>
      </div>
    );
  }

  const center = [28.6195, 77.0440];

  // Classes change when full-screen mode is active (mobile only)
  const containerCls = fullscreen
    ? "fixed inset-0 z-[9999] bg-white"
    : "min-h-screen bg-white";
  const wrapperCls = fullscreen
    ? "h-full w-full"
    : "max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6";
  const mapBoxCls = fullscreen
    ? "w-full h-full border-0"
    : "w-full h-[70vh] border border-[#E4E4E7] rounded-sm overflow-hidden";

  return (
    <div className={containerCls} data-testid="map-page">
      <div className={wrapperCls}>
        {!fullscreen && (
          <>
            {/* v2.71.51 — Integrated page header replaces the floating Back pill. */}
            <PageHeader
              title="Map view"
              subtitle={`${properties.length} ${ownOnly ? "of your listings" : "properties"} on map`}
              backLabel="Home"
              home="/"
              borderless
              className="-mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 mb-4"
            />
            {ownOnly && (
              <div className="mb-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#EEF4FF] text-[#0044CC] border border-[#C7D9FF] px-1.5 py-0.5 rounded-sm font-semibold">my listings only</div>
            )}
            {/* v2.71.76 — Data-quality banner: surface properties that exist in
                the inventory but are missing coordinates and therefore CANNOT
                render as map markers. Without this the user thinks listings
                are "missing" when they're really just un-geocoded. */}
            {!ownOnly && meta?.without_coords > 0 && (
              <div
                className="mb-4 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm"
                data-testid="map-coord-warning"
              >
                <strong>{meta.without_coords}</strong> {meta.without_coords === 1 ? "property is" : "properties are"} hidden because {meta.without_coords === 1 ? "it is" : "they are"} missing location coordinates. Showing <strong>{properties.length}</strong> of <strong>{meta.total}</strong> visible to your role.
              </div>
            )}
          </>
        )}
        <div className={`${mapBoxCls} relative`} data-testid="map-container">
          {/* Fullscreen toggle — mobile only */}
          <button
            onClick={() => setFullscreen((v) => !v)}
            className="md:hidden absolute top-3 right-3 z-[1000] bg-white shadow-md border border-[#E4E4E7] rounded-sm w-11 h-11 flex items-center justify-center active:bg-[#F4F4F5]"
            data-testid="map-fullscreen-toggle"
            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          {fullscreen && (
            <div className="md:hidden absolute top-3 left-3 z-[1000] bg-white/95 backdrop-blur px-3 py-2 rounded-sm text-[11px] font-semibold text-[#09090B] border border-[#E4E4E7]">
              {properties.length} properties
            </div>
          )}
          <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {properties.map((p) => (
              <Marker key={p.id} position={[p.latitude, p.longitude]} icon={priceIcon(formatINR(p.price))}>
                <Popup>
                  <div style={{ width: 220 }}>
                    {p.images && p.images[0] && (
                      <img src={resolveImage(p.images[0], { w: 300 })} alt={p.title} style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 3, marginBottom: 6 }} loading="lazy" decoding="async" />
                    )}
                    <div style={{ fontSize: 10, letterSpacing: 1, color: "#52525B", textTransform: "uppercase", fontWeight: 600 }}>
                      {p.area} · {p.bhk}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2, marginBottom: 4, fontFamily: "Outfit,sans-serif" }}>
                      {p.title}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6 }}>
                      <span style={{ color: "#0055FF", fontWeight: 700 }}>{formatINR(p.price)}</span>
                      <span style={{ color: "#15803D", fontWeight: 600 }}>ROI {p.roi?.toFixed(1)}%</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#52525B" }}>Metro: {formatDistance(p.distance_metro_m)}</div>
                    <Link to={`/property/${p.id}`} style={{ display: "inline-block", marginTop: 8, fontSize: 11, fontWeight: 700, color: "#0055FF" }} data-testid={`map-view-details-${p.id}`}>
                      View Details →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
